// import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "../../../../assets/media/icons";
import { CurrencyContext } from "../../../../contexts/currency";
import { BookingOffer } from "../../../../definitions/booking";
import { PropertyRoom } from "../../../../definitions/property";
import Flex from "../../../structure/Flex";
import Amenities from "../../Amenities";
import Button from "../../Button";
import { AmountSelector } from "../../Form";
import AvailabilityCalendar from "./../AvailabilityCalendar";

interface Room extends PropertyRoom {
  offerData?: BookingOffer;
}
interface RoomCardProps {
  room: Room;
  count: number;
  setCount: (count: number) => void;
  loadingAvailability: boolean;
  operatorCode?: string;
  checkInDate?: string;
  checkOutDate?: string;
  setGalleryVisible?: any;
  setGalleryData?: any;
}

const RoomCard: React.FC<RoomCardProps> = ({
  room,
  count,
  setCount,
  loadingAvailability,
  operatorCode,
  checkInDate,
  checkOutDate,
  setGalleryVisible,
  setGalleryData,
}) => {
  const { formatCurrency } = useContext(CurrencyContext);

  const [isAvailabilityCalendarVisible, setAvailabilityCalendarVisibility] =
    useState<boolean>(false);
  // const [overTwoMonths, setOverTwoMonths] = useState<boolean>(false);

  const handleRoomSelect: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    setCount(1);
  };

  const toggleAvailabilityCalendarVisibility = () => {
    setAvailabilityCalendarVisibility((v) => !v);
  };

  useEffect(() => {
    if (room.availability && !room.availability.isAvailable) {
      setCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.availability]);

  useEffect(() => {
    setAvailabilityCalendarVisibility(false);
  }, [operatorCode, checkInDate]);

  useEffect(() => {
    if (
      room.availability !== null &&
      room.availability?.isAvailable === false
    ) {
      setAvailabilityCalendarVisibility(true);
    }
  }, [room.availability]);

  // useEffect(() => {
  //   const dateDiff = moment(checkOutDate, "YYYY-MM-DD").diff(moment(), "days");
  //   setOverTwoMonths(dateDiff > 60 ? true : false);
  // }, [checkOutDate]);

  const handleClickView = () => {
    const result = [];

    result?.unshift({
      title: room?.name,
      images: (room?.images ?? []).map((image) => image.image),
    });
    setGalleryData(result);
    setGalleryVisible();
  };

  return room != null ? (
    <>
      <section
        className={`mt-4 overflow-hidden w-full shadow-xs hover:shadow-md ${
          isAvailabilityCalendarVisible ? "rounded-t-lg" : "rounded-lg"
        }`}
      >
        <Flex className="flex-col sm:flex-row">
          <div
            onClick={handleClickView}
            className="flex-shrink-0 w-full h-40 sm:w-48 sm:h-auto bg-subtitle"
          >
            {room.images && room.images.length > 0 && (
              <img
                src={`${room.images[0].image}
                   `}
                alt={room.images[0].title}
                className={`w-full ${"h-full"} ${"object-cover"} object-center cursor-zoom-in`}
              />
            )}
          </div>
          <div
            className="flex flex-col flex-grow w-auto px-4 pt-3 pb-4 cursor-pointer bg-gradient-to-t from-white to-gray-200"
            style={{ minHeight: window.innerWidth < 640 ? "" : "10rem" }}
            onClick={toggleAvailabilityCalendarVisibility}
          >
            <div className="flex-grow mb-2 sm:mb-0">
              <Flex items="start" justify="between">
                <Flex col>
                  <span className="block font-medium leading-snug sm:text-xl">
                    {room.name}
                  </span>
                  <span className="flex items-center mt-1 text-sm">
                    <i className="material-icons">person</i> x {room.occupancy}
                  </span>
                </Flex>
                {room.availability?.isAvailable && (
                  <Flex col items="center" className="flex-shrink-0 pl-4">
                    <span className="font-medium whitespace-no-wrap sm:text-xl">
                      {formatCurrency(
                        (room.price - (room.discount || 0)) / room.nights
                      )}
                      {room.discount != null && room.discount > 0 && (
                        <span className="pl-1 text-sm font-normal line-through text-text">
                          {formatCurrency(room.price / room.nights)}
                        </span>
                      )}
                      <span className="text-xs font-medium text-text">
                        {" "}
                        / night
                      </span>
                    </span>
                    {room.slab_active && (
                      <span className="px-2 py-1 text-xs font-medium leading-none tracking-wide text-green-500 uppercase bg-green-200 border border-green-500 rounded-full">
                        long-stay price
                      </span>
                    )}
                    {room.discount != null && Math.abs(room.discount) > 0 && (
                      <span className="px-2 py-1 text-xs font-medium leading-none tracking-wide text-green-500 uppercase bg-green-200 border border-green-500 rounded-full">
                        {room.offerData?.tag || "Offer applied"}
                      </span>
                    )}
                  </Flex>
                )}
              </Flex>
              {room.description && (
                <span
                  className="block my-2 text-sm leading-5 whitespace-pre-wrap text-text"
                  dangerouslySetInnerHTML={{ __html: room.description }}
                ></span>
              )}
            </div>
            <span className="flex items-center text-xs sm:text-sm text-subtitle">
              {room.amenities && (
                <Amenities
                  list={room.amenities}
                  iconOnly
                  amenityClassname="pr-2 pb-2 text-text"
                />
              )}
              {/* <Icon>keyboard_arrow_down</Icon> Amenities */}
            </span>
            <div className="flex items-end justify-between flex-shrink-0">
              <span className="flex items-center text-xs font-medium cursor-pointer text-orange">
                Availability calendar
                {isAvailabilityCalendarVisible ? (
                  <ChevronUp className="w-4 h-4 ml-1" />
                ) : (
                  <ChevronDown className="w-4 h-4 ml-1" />
                )}
              </span>
              {loadingAvailability || room.availability == null ? (
                <span className="text-sm whitespace-no-wrap text-subtitle">
                  Checking availability ...
                </span>
              ) : room.availability.isBookable ? (
                room.availability.isAvailable ? (
                  count ? (
                    <AmountSelector
                      value={count}
                      setValue={setCount}
                      maxValue={room.availability.maxUnits}
                    />
                  ) : (
                    <Button
                      size="sm"
                      // disabled={overTwoMonths}
                      onClick={handleRoomSelect}
                    >
                      Select{" "}
                      {room.sub_category === "dorm-bed"
                        ? "Bed"
                        : room.sub_category === "private-room"
                        ? "Room"
                        : "Unit"}
                    </Button>
                  )
                ) : (
                  <span className="font-black tracking-wide uppercase whitespace-no-wrap sm:text-xl text-subtitle">
                    Sold Out
                  </span>
                )
              ) : (
                <span className="flex flex-col items-center whitespace-no-wrap sm:text-xl text-subtitle">
                  <span className="font-black leading-none uppercase">
                    Unavailable
                  </span>
                  <span className="text-xs font-medium leading-none tracking-wide uppercase">
                    At the Moment
                  </span>
                </span>
              )}
            </div>
          </div>
        </Flex>
      </section>
      {isAvailabilityCalendarVisible && (
        <AvailabilityCalendar
          inventoryId={room.id}
          operatorCode={operatorCode || ""}
          checkInDate={checkInDate || ""}
        />
      )}
    </>
  ) : (
    <></>
  );
};

export default RoomCard;
